import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  Divider,
} from '@mui/material';
import { Expense, ExpenseStatus} from '../types/accounting';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';

interface MyExpenseDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  expense: Expense | null;
}

const MyExpenseDetailsModal: React.FC<MyExpenseDetailsModalProps> = ({ open, onClose, onSave, expense }) => {
  const [status, setStatus] = useState<any>('');
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);

  // Sync state when expense opens
  useEffect(() => {
    if (expense) {
      setStatus(expense.status);
      setRemark(expense.remark || '');
    }
  }, [expense]);

  if (!expense) return null;

  const handleUpdate = async () => {
    try {
      setLoading(true);
      // Ensure your service has an updateExpense method
      await accountingService.updateExpense(expense.id, { status, remark });
      onSave(); // Refresh the list in the parent page
      onClose();
    } catch (err) {
      console.error("Update failed", err);
      alert("Failed to update expense");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Expense Details</DialogTitle>
      <DialogContent dividers>
        
        {/* Read Only Fields */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Submitted By:</Typography>
          <Typography>{expense.submitted_by_details?.first_name} {expense.submitted_by_details?.last_name || 'N/A'}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Date:</Typography>
          <Typography>{format(new Date(expense.date), 'MMM dd, yyyy')}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Project:</Typography>
          <Typography>{expense.project_details?.name || 'N/A'}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Expense Type:</Typography>
          <Typography>{expense.expense_type}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Description:</Typography>
          <Typography>{expense.description || 'No description'}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Amount:</Typography>
          <Typography variant="h6" color="primary">${parseFloat(expense.amount).toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Editable Fields */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Update Status</Typography>
        
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            fullWidth
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="PAID">Paid</MenuItem>
            <MenuItem value="PAYABLE">Payable</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Remark"
            multiline
            rows={2}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button 
          onClick={handleUpdate} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyExpenseDetailsModal;